import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidV4 } from 'uuid';

import TargetIcon from '../../assets/svgs/TargetIcon';

import roundToPrecision from '../../utils/roundToPrecision';
import EDIT_TOOL_TYPES from '../../data/edit-tool-types';

import {
  actionShape,
  annotationShape,
  annotationTypeShape,
} from '../../utils/propTypesShapes';

import { AnnotationCanvasStyle } from './AnnotationCanvasStyle';

const EXAM_IMAGE_SIZE = 512;

const AnnotationCanvas = ({
  slice,
  eraser,
  tool,
  orignalRef,
  subtype,
  type,
  actionsList,
  annotations,
  undoActionsList,
  updateActionsList,
  updateAnnotationList,
  updateGuidePosition,
  updateShowGuide,
}) => {
  const annotationActionRef = useRef();

  const annotationsBySlice = useMemo(() => {
    const current = annotations?.filter(
      (annotation) => annotation?.slice === slice
    );

    if (current?.length) {
      return current;
    }

    return [];
  }, [annotations, slice]);

  const selectedAnnotation = useMemo(() => {
    if (subtype) {
      return subtype;
    }

    if (type) {
      return type;
    }

    return null;
  }, [subtype, type]);

  const undoPointsId = useMemo(() => {
    const current = [];

    if (undoActionsList?.length) {
      undoActionsList?.forEach((action) => {
        action?.points?.forEach((point) => current.push(point.id));
      });
    }

    return current;
  }, [undoActionsList]);

  const onMouseMoveAnnotation = (event) => {
    if (orignalRef?.current && annotationActionRef?.current) {
      const { clientHeight } = orignalRef?.current;
      const clientRect = annotationActionRef?.current?.getBoundingClientRect();
      const fixY = roundToPrecision((clientHeight - EXAM_IMAGE_SIZE) / 2, 0);
      let x = 0;
      let y = 0;

      x += event?.clientX || 0;
      x -= clientRect?.left || 0;
      x = x > 0 ? x : 0;
      y += event?.clientY || 0;
      y -= clientRect?.top || 0;
      y += fixY || 0;
      y = y > 0 ? y : 0;

      updateGuidePosition({ x, y });
    }
  };

  const onClickAnnotationAction = (event) => {
    if (
      annotationActionRef?.current &&
      selectedAnnotation &&
      slice?.length &&
      !eraser
    ) {
      const clientRect = annotationActionRef?.current?.getBoundingClientRect();
      let x = 0;
      let y = 0;

      x += event?.clientX || 0;
      x -= clientRect?.left || 0;
      x = x > 0 ? x : 0;
      y += event?.clientY || 0;
      y -= clientRect?.top || 0;
      y = y > 0 ? y : 0;

      const currentAnnotation = {
        id: uuidV4(),
        x,
        y,
        type: selectedAnnotation,
        slice,
      };

      updateAnnotationList([...annotations, currentAnnotation]);
      updateActionsList([
        ...actionsList,
        {
          id: uuidV4(),
          type: EDIT_TOOL_TYPES?.annotation?.key,
          points: [currentAnnotation],
        },
      ]);
    }
  };

  const onClickSingleAnnotation = (singleAnnotationId) => {
    if (singleAnnotationId) {
      const currentAnnotationList = annotations.filter(
        (annotation) => annotation?.id !== singleAnnotationId
      );

      updateAnnotationList(currentAnnotationList);
    }
  };

  if (orignalRef && slice?.length) {
    return (
      <AnnotationCanvasStyle>
        {tool === EDIT_TOOL_TYPES?.annotation?.key &&
        annotationsBySlice?.length ? (
          <div className="annotation-list-container">
            {annotationsBySlice?.map((annotation) => {
              if (annotation && !undoPointsId.includes(annotation?.id)) {
                return (
                  <button
                    key={annotation?.id}
                    className="annotation-item"
                    type="button"
                    style={{
                      backgroundColor: annotation?.type?.color,
                      left: `${annotation?.x}px`,
                      top: `${annotation?.y}px`,
                    }}
                    onClick={() => onClickSingleAnnotation(annotation?.id)}
                  >
                    <TargetIcon />
                  </button>
                );
              }

              return null;
            })}
          </div>
        ) : null}
        {tool === EDIT_TOOL_TYPES?.annotation?.key ? (
          <button
            ref={annotationActionRef}
            className={`annotation-button${eraser ? ' hide' : ''}`}
            type="button"
            disabled={tool !== EDIT_TOOL_TYPES?.annotation?.key}
            aria-label="Original actions"
            onClick={onClickAnnotationAction}
            onMouseEnter={() => updateShowGuide(true)}
            onMouseLeave={() => updateShowGuide(false)}
            onMouseMove={onMouseMoveAnnotation}
          />
        ) : null}
      </AnnotationCanvasStyle>
    );
  }

  return null;
};

AnnotationCanvas.propTypes = {
  slice: PropTypes.string,
  eraser: PropTypes.bool,
  tool: PropTypes.oneOf(['', ...Object.keys(EDIT_TOOL_TYPES)]),
  orignalRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  subtype: PropTypes.oneOfType([PropTypes.oneOf([null]), annotationTypeShape]),
  type: PropTypes.oneOfType([PropTypes.oneOf([null]), annotationTypeShape]),
  actionsList: PropTypes.arrayOf(actionShape),
  annotations: PropTypes.arrayOf(annotationShape),
  undoActionsList: PropTypes.arrayOf(actionShape),
  updateActionsList: PropTypes.func.isRequired,
  updateAnnotationList: PropTypes.func.isRequired,
  updateGuidePosition: PropTypes.func.isRequired,
  updateShowGuide: PropTypes.func.isRequired,
};

AnnotationCanvas.defaultProps = {
  slice: '',
  eraser: false,
  tool: '',
  subtype: null,
  type: null,
  annotations: [],
  actionsList: [],
  undoActionsList: [],
};

export default AnnotationCanvas;
